/**
 * GiAbsorptionCard — iter1047 (gastro1)
 * Абсорбция по етажи: стомах → дуоденум → йеюнум → илеум → колон.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2, AlertTriangle, ArrowDownUp } from "lucide-react";

const STATUS_META = {
  suspect: { label: "Слабо място", cls: "bg-rose-500 text-white" },
  watch: { label: "Следи се", cls: "bg-amber-500 text-white" },
  ok: { label: "Работи", cls: "bg-emerald-500 text-white" },
  no_data: { label: "Няма данни", cls: "bg-slate-400 text-white" },
};

export const GiAbsorptionCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openScreen, setOpenScreen] = useState(false);
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);

  useEffect(() => {
    axios.get(`${API}/gi/absorption`, { timeout: 25000 })
      .then(({ data: d }) => { setData(d); setSel(d.screen_selected || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [API]);

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await axios.post(`${API}/gi/screen`, { items: sel });
      setData(d);
      setOpenScreen(false);
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl border ${card}`} data-testid="gi-absorption-loading">
        <Loader2 className="w-4 h-4 animate-spin text-teal-500" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className={`p-4 rounded-2xl border mb-4 ${card}`} data-testid="gi-absorption-card">
      <div className="flex items-center gap-2 mb-1">
        <ArrowDownUp className="w-4 h-4 text-teal-500" />
        <h2 className="font-bold text-sm">Абсорбция по етажи — къде се губи усвояването</h2>
      </div>
      <p className="text-[11px] opacity-70 mb-3" data-testid="gi-summary">{data.summary_bg}</p>

      {(data.red_flags || []).map((f) => (
        <div
          key={f.key}
          data-testid={`gi-flag-${f.key}`}
          className={`p-3 rounded-xl mb-2 border-2 ${
            f.urgency === "emergency"
              ? (isDark ? "bg-rose-900/40 border-rose-500" : "bg-rose-50 border-rose-400")
              : (isDark ? "bg-amber-900/30 border-amber-500/60" : "bg-amber-50 border-amber-300")
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${f.urgency === "emergency" ? "text-rose-500" : "text-amber-500"}`} />
            <div className="min-w-0">
              <div className="text-xs font-bold">{f.title_bg}</div>
              <p className="text-[11px] mt-1 leading-relaxed opacity-90">{f.text_bg}</p>
              <div className="text-[11px] mt-1.5 font-semibold">{f.action_bg}</div>
            </div>
          </div>
        </div>
      ))}

      {/* Изхождания и транзит */}
      {data.stool && (
        <div className={`p-3 rounded-xl mb-3 ${isDark ? "bg-slate-800/40" : "bg-slate-50 border border-slate-200"}`}
             data-testid="gi-stool">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[11px] font-bold">{data.stool.label_bg}</span>
            <span className="text-[10px] opacity-70" data-testid="gi-stool-transit">
              транзит: {data.stool.transit_bg}
              {data.stool.avg_bristol != null && ` · Bristol ${data.stool.avg_bristol}`}
              {data.stool.count ? ` · ${data.stool.count} записа` : ""}
            </span>
          </div>
          <p className="text-[11px] leading-relaxed opacity-90">{data.stool.text_bg}</p>
          {(data.stool.levers || []).length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {data.stool.levers.map((l) => (
                <li key={l} className="text-[10px] opacity-80">• {l}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* iter1048 — екзокринен панкреас (Фаза 2) */}
      {data.pancreas && (
        <div className={`rounded-xl border mb-3 ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
             data-testid="gi-pancreas">
          <button
            onClick={() => setOpen(open === "pancreas" ? null : "pancreas")}
            data-testid="gi-pancreas-toggle"
            className="w-full text-left p-3 flex items-center gap-2"
          >
            <span className="text-[11px] font-bold flex-1 min-w-0">{data.pancreas.title_bg}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ${
              (STATUS_META[data.pancreas.status] || STATUS_META.no_data).cls
            }`} data-testid="gi-pancreas-status">
              {(STATUS_META[data.pancreas.status] || STATUS_META.no_data).label}
            </span>
            <span className="text-[10px] opacity-50">{open === "pancreas" ? "−" : "+"}</span>
          </button>
          {open === "pancreas" && (
            <div className="px-3 pb-3 space-y-2">
              <div className="text-[11px] font-semibold">{data.pancreas.label_bg}</div>
              <div className="text-[10px] opacity-60">Прави: {data.pancreas.absorbs_bg}</div>
              <p className="text-[11px] leading-relaxed opacity-90">{data.pancreas.explain_bg}</p>
              <p className="text-[10px] italic opacity-75" data-testid="gi-pancreas-mechanism">
                {data.pancreas.mechanism_bg}
              </p>
              {(data.pancreas.signals || []).length > 0 && (
                <ul className="space-y-0.5">
                  {data.pancreas.signals.map((s) => (
                    <li key={s} className="text-[10px] opacity-80">• {s}</li>
                  ))}
                </ul>
              )}
              {(data.pancreas.missing || []).length > 0 && (
                <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                     data-testid="gi-pancreas-missing">
                  <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                  {data.pancreas.missing.map((m) => (
                    <div key={m.marker} className="mb-0.5">
                      <strong>{m.label_bg}</strong> — {m.why_bg}
                    </div>
                  ))}
                </div>
              )}
              {(data.pancreas.links || []).map((l, i) => (
                <button
                  key={`panc-${i}`}
                  onClick={() => navigate(l.route)}
                  data-testid={`gi-pancreas-link-${i}`}
                  className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                    l.urgency === "high"
                      ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                      : (isDark ? "bg-teal-500/10 hover:bg-teal-500/20" : "bg-teal-50 hover:bg-teal-100")
                  }`}
                >
                  <span className="font-bold">{l.title_bg} →</span>
                  <span className="opacity-80"> {l.action_bg}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Етажите */}
      <div className="space-y-2">
        {(data.floors || []).map((f) => {
          const meta = STATUS_META[f.status] || STATUS_META.no_data;
          const isOpen = open === f.key;
          return (
            <div
              key={f.key}
              className={`rounded-xl border ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
              data-testid={`gi-floor-${f.key}`}
            >
              <button
                onClick={() => setOpen(isOpen ? null : f.key)}
                data-testid={`gi-floor-toggle-${f.key}`}
                className="w-full text-left p-3 flex items-center gap-2"
              >
                <span className="text-[11px] font-bold flex-1 min-w-0">{f.title_bg}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ${meta.cls}`}
                      data-testid={`gi-floor-status-${f.key}`}>
                  {meta.label}
                </span>
                <span className="text-[10px] opacity-50">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-[11px] font-semibold">{f.label_bg}</div>
                  <div className="text-[10px] opacity-60">Усвоява: {f.absorbs_bg}</div>
                  <p className="text-[11px] leading-relaxed opacity-90">{f.explain_bg}</p>
                  {(f.signals || []).length > 0 && (
                    <ul className="space-y-0.5" data-testid={`gi-signals-${f.key}`}>
                      {f.signals.map((s) => (
                        <li key={s} className="text-[10px] opacity-80">• {s}</li>
                      ))}
                    </ul>
                  )}
                  {(f.levers || []).length > 0 && (
                    <ul className="space-y-0.5">
                      {f.levers.map((s) => (
                        <li key={s} className="text-[10px] font-semibold opacity-85">→ {s}</li>
                      ))}
                    </ul>
                  )}
                  {(f.missing || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid={`gi-missing-${f.key}`}>
                      <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                      {f.missing.map((m) => (
                        <div key={m.marker} className="mb-0.5">
                          <strong>{m.label_bg}</strong> — {m.why_bg}
                        </div>
                      ))}
                    </div>
                  )}
                  {(f.links || []).map((l, i) => (
                    <button
                      key={`${f.key}-${i}`}
                      onClick={() => navigate(l.route)}
                      data-testid={`gi-link-${f.key}-${i}`}
                      className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                        l.urgency === "high"
                          ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                          : (isDark ? "bg-teal-500/10 hover:bg-teal-500/20" : "bg-teal-50 hover:bg-teal-100")
                      }`}
                    >
                      <span className="font-bold">{l.title_bg} →</span>
                      <span className="opacity-80"> {l.action_bg}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Кратка проверка */}
      <button
        onClick={() => setOpenScreen((v) => !v)}
        data-testid="gi-screen-toggle"
        className={`mt-3 w-full text-[11px] font-semibold py-2 rounded-lg transition-colors ${
          isDark ? "bg-teal-500/15 text-teal-200 hover:bg-teal-500/25"
                 : "bg-teal-50 text-teal-700 hover:bg-teal-100"
        }`}
      >
        {openScreen ? "Скрий въпросите" : `Кратка проверка на храносмилането (${(data.screen_selected || []).length} отбелязани)`}
      </button>
      {openScreen && (
        <div className="mt-2 space-y-1.5" data-testid="gi-screen">
          {(data.screen_items || []).map((it) => (
            <label key={it.key}
                   data-testid={`gi-screen-${it.key}`}
                   className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-[11px] ${
                     sel.includes(it.key)
                       ? (isDark ? "bg-teal-500/20" : "bg-teal-50")
                       : (isDark ? "bg-slate-800/40" : "bg-slate-50")
                   }`}>
              <input type="checkbox" checked={sel.includes(it.key)}
                     onChange={() => toggle(it.key)} className="mt-0.5" />
              <span>
                <span className="opacity-50 text-[9px] uppercase font-bold block">{it.group_bg}</span>
                {it.label_bg}
              </span>
            </label>
          ))}
          <button
            onClick={save}
            disabled={saving}
            data-testid="gi-screen-save"
            className="w-full mt-1 py-2 rounded-lg bg-teal-600 text-white text-[11px] font-bold disabled:opacity-60"
          >
            {saving ? "Записвам…" : "Запиши"}
          </button>
        </div>
      )}

      {(data.medications || []).length > 0 && (
        <div className={`mt-3 p-2.5 rounded-lg text-[10px] ${isDark ? "bg-slate-800/50" : "bg-slate-50 border border-slate-200"}`}
             data-testid="gi-meds">
          <div className="font-bold opacity-70 mb-1">Лекарства от Вашия списък, които влияят на усвояването</div>
          {data.medications.map((m) => (
            <div key={m.name} className="mb-1">
              <strong>{m.name}</strong> ({m.group_bg}) — {m.why_bg}
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] italic opacity-60 mt-3" data-testid="gi-relay-note">
        {data.relay_bg}
      </p>
    </div>
  );
};

export default GiAbsorptionCard;
